/* Built from the checksummed portal-preview cart and app sources. */
(() => {
"use strict";
const emptyCart = () => ({ items: {} });

function normalizeApiBase(value, origin) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (!raw.startsWith("/") || raw.startsWith("//")) throw new Error("Invalid same-origin API base");
  const parsed = new URL(raw, origin);
  if (parsed.origin !== origin || parsed.search || parsed.hash) {
    throw new Error("Invalid same-origin API base");
  }
  return parsed.pathname.replace(/\/$/, "");
}

const positiveInteger = (value) => Number.isSafeInteger(value) && value > 0;

const copyItems = (state) => ({ ...state.items });

function normalizedItem(item) {
  if (!item || (item.mode !== "three" && item.mode !== "sizes")) {
    throw new TypeError("Invalid cart item");
  }
  if (item.mode === "three") {
    if (!positiveInteger(item.threeCount)) throw new RangeError("threeCount must be a positive integer");
    return { mode: "three", threeCount: item.threeCount, quantities: { ...(item.quantities || {}) } };
  }
  const quantities = Object.fromEntries(
    Object.entries(item.quantities || {}).filter(([, quantity]) => positiveInteger(quantity)),
  );
  if (Object.keys(quantities).length === 0) throw new RangeError("Choose at least one size quantity");
  return { mode: "sizes", threeCount: positiveInteger(item.threeCount) ? item.threeCount : 1, quantities };
}

function cartReducer(state, action) {
  if (!state || !state.items || !action || typeof action.type !== "string") {
    throw new TypeError("Invalid cart reducer input");
  }
  const items = copyItems(state);
  const current = items[action.productId];
  switch (action.type) {
    case "ADD_THREE":
      items[action.productId] = {
        mode: "three",
        threeCount: current?.threeCount ? current.threeCount + 1 : 1,
        quantities: current?.quantities || {},
      };
      return { items };
    case "SET_THREE":
      if (!positiveInteger(action.value)) throw new RangeError("threeCount must be a positive integer");
      items[action.productId] = { mode: "three", threeCount: action.value, quantities: current?.quantities || {} };
      return { items };
    case "SET_MODE":
      if (!current) throw new Error("Product is not in the cart");
      if (action.mode !== "three" && action.mode !== "sizes") throw new RangeError("Unknown cart mode");
      items[action.productId] = {
        mode: action.mode,
        threeCount: current.threeCount || 1,
        quantities: current.quantities || {},
      };
      return { items };
    case "SET_SIZE": {
      if (!current || current.mode !== "sizes") throw new Error("Size quantities are not active");
      if (!Number.isSafeInteger(action.value) || action.value < 0) {
        throw new RangeError("Size quantity must be a nonnegative integer");
      }
      const quantities = { ...current.quantities };
      if (action.value === 0) delete quantities[action.variantId];
      else quantities[action.variantId] = action.value;
      items[action.productId] = { ...current, quantities };
      return { items };
    }
    case "REMOVE":
      delete items[action.productId];
      return { items };
    case "UPSERT_ITEM":
      items[action.productId] = normalizedItem(action.item);
      return { items };
    case "CLEAR":
      return emptyCart();
    default:
      throw new RangeError(`Unknown cart action: ${action.type}`);
  }
}

function quoteItems(state) {
  return Object.entries(state.items).map(([productId, item]) => {
    if (item.mode === "three") {
      if (!positiveInteger(item.threeCount)) throw new RangeError("Invalid three count");
      return { productId, threeCount: item.threeCount };
    }
    const quantities = Object.fromEntries(
      Object.entries(item.quantities).filter(([, quantity]) => positiveInteger(quantity)),
    );
    if (Object.keys(quantities).length === 0) throw new RangeError("Choose at least one size quantity");
    return { productId, quantities };
  });
}

const root = document.getElementById("wella-wholesale");
const $ = (selector) => root.querySelector(selector);
const apiBase = normalizeApiBase(root.dataset.apiBase, window.location.origin);
const phoneMode = true;
const apiPath = (path) => `${apiBase}/api/${path}`;
const state = {
  session: null,
  catalog: [],
  collections: [],
  cart: emptyCart(),
  quote: null,
  orders: [],
  quoteRevision: 0,
  quoteInFlight: false,
  quoteWanted: false,
  lastCartEditAt: 0,
  pendingOrderKey: null,
  isSubmitting: false,
  catalogMode: "cards",
  catalogFilter: "all",
  detailProductId: null,
  guided: { step: 1, productId: null, drafts: {} },
};
let sessionEpoch = 0;
let collectionLoadController;

const views = ["#loading-view", "#access-view", "#suspended-view", "#approved-view"];
const showView = (selector) => views.forEach((item) => { $(item).hidden = item !== selector; });

function message(text, type = "") {
  const element = $("#global-message");
  element.textContent = text || "";
  element.className = `message ${type}`.trim();
  element.hidden = !text;
}

async function api(path, options = {}) {
  if (phoneMode) {
    const operation = path === "orders" && options.method === "POST" ? "submit" : path;
    const payload = options.body ? JSON.parse(options.body) : undefined;
    return window.wellaWholesalePhoneAuth.api(operation, payload);
  }
  const headers = new Headers(options.headers || {});
  if (options.body) headers.set("Content-Type", apiBase.startsWith("/apps/") ? "text/plain; charset=utf-8" : "application/json");
  if (state.session?.csrfToken && options.method && options.method !== "GET") {
    headers.set("X-CSRF-Token", state.session.csrfToken);
  }
  const response = await fetch(apiPath(path), { credentials: "same-origin", ...options, headers });
  let payload = null;
  try { payload = await response.json(); } catch { payload = null; }
  if (!response.ok) throw new Error(payload?.message || "تعذر إكمال الطلب. حاول تاني.");
  return payload;
}

const money = (value, currency) => new Intl.NumberFormat("ar-EG", {
  style: "currency", currency, minimumFractionDigits: 0, maximumFractionDigits: 0,
}).format(value);

const readableDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat("ar-EG", {
    dateStyle: "medium", timeStyle: "short", timeZone: "Africa/Cairo",
  }).format(date);
};

function node(tag, className, text) {
  const element = document.createElement(tag);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

function productById(productId) { return state.catalog.find((product) => String(product.id) === String(productId)); }

function validateCatalog(products) {
  if (!Array.isArray(products)) throw new Error("بيانات الكتالوج غير صالحة.");
  products.forEach((product) => {
    const validId = typeof product?.id === "string" || Number.isSafeInteger(product?.id);
    const validSizes = Array.isArray(product?.sizes) && product.sizes.length > 0 && product.sizes.every((size) =>
      (typeof size.id === "string" || Number.isSafeInteger(size.id)) && typeof size.name === "string" &&
      Number.isFinite(Number(size.unitPrice ?? product.unitPriceFrom)) && Number(size.unitPrice ?? product.unitPriceFrom) >= 0);
    if (!validId || !product.name || !product.modelCode || !validSizes || !Number.isFinite(Number(product.threePrice)) || !product.currency) {
      throw new Error("بيانات الكتالوج ناقصة أو غير صالحة.");
    }
  });
  return products;
}

/* Phase 2 (decisions B/C): price before discount, «خصمك X%», price after discount (whole pounds) + missing-sizes badge. */
function renderDiscountLine(product, three) {
  const pct = Number(product.discountPercent || 0);
  const before = three ? product.threePriceBefore : product.unitPriceFromBefore;
  if (!(pct > 0) || !Number.isFinite(Number(before))) return null;
  const line = node("div", "price-discount-line");
  line.append(node("s", "price-before", money(before, product.currency)), node("span", "price-discount-pct", ` خصمك ${pct % 1 ? pct.toFixed(1) : pct}%`));
  return line;
}
function renderBadge(product) {
  return product.badge ? node("div", "missing-sizes-badge", product.badge) : null;
}

function productCategory(product) {
  if (root.dataset.collectionMode === "shopify") {
    return state.collections.filter((collection) => collection.productIds.has(String(product.id))).map((collection) => collection.title).join(" · ");
  }
  if (product.category) return product.category;
  const name = String(product.name || "");
  if (name.includes("عباية")) return "عبايات";
  if (name.includes("قميص") || name.includes("قمصان")) return "قمصان";
  if (name.includes("بيجامة") || name.includes("بيجامات") || name.includes("بيجاما")) return "بيجامات";
  if (name.includes("مكتب")) return "مكاتب";
  return "أخرى";
}

function categoryEnabled(category) {
  const key = { "عبايات": "Abayas", "بيجامات": "Pajamas", "قمصان": "Shirts" }[category] || "Other";
  return root.dataset[`show${key}`] !== "false";
}

function enabledCatalog() {
  if (root.dataset.collectionMode === "shopify") return state.catalog;
  return state.catalog.filter((product) => categoryEnabled(productCategory(product)));
}

function visibleCatalog() {
  if (root.dataset.collectionMode === "shopify") {
    const collection = state.collections.find((item) => item.id === state.catalogFilter);
    return state.catalog.filter((product) => state.catalogFilter === "all" || collection?.productIds.has(String(product.id)));
  }
  return enabledCatalog().filter((product) => state.catalogFilter === "all" || productCategory(product) === state.catalogFilter);
}

async function loadCollectionSelection(catalog, signal) {
  if (root.dataset.collectionMode !== "shopify") return {catalog, collections: []};
  const selected = [...root.querySelectorAll("[data-wholesale-collection]")].map((element) => ({
    id: element.dataset.collectionId, title: element.dataset.collectionTitle,
    url: element.dataset.collectionUrl, imageUrl: element.dataset.collectionImage || "",
  }));
  if (selected.length > 12 || new Set(selected.map((item) => item.id)).size !== selected.length) throw new Error("إعداد الكوليكشنز غير صالح.");
  const collections = new Array(selected.length);
  let next = 0;
  async function worker() {
    while (next < selected.length) {
      const index = next++;
      const item = selected[index];
      const url = new URL(item.url, window.location.origin);
      if (!/^\d+$/.test(item.id || "") || !item.title || url.origin !== window.location.origin || !/\/collections\/[^/]+\/?$/.test(url.pathname) || url.search || url.hash) throw new Error("إعداد الكوليكشنز غير صالح.");
      const ids = new Set(); let pages = 1; let total = null;
      for (let page = 1; page <= pages; page++) {
        if (signal.aborted) throw new Error("انتهت جلسة التحميل.");
        url.searchParams.set("view", "wella-wholesale-members"); url.searchParams.set("page", String(page));
        const response = await fetch(url.href, {credentials: "same-origin", signal, cache: "no-store", redirect: "error"});
        if (!response.ok) throw new Error("تعذر تحميل الكوليكشنز المختارة.");
        const data = await response.json();
        if (data.schema !== 1 || data.collectionId !== item.id || data.page !== page || !Number.isSafeInteger(data.pages) || data.pages < 1 || data.pages > 100 || !Number.isSafeInteger(data.total) || data.total < 0 || data.total > 25000 || data.pages !== Math.max(1, Math.ceil(data.total / 250)) || !Array.isArray(data.productIds) || data.productIds.length > 250 || data.productIds.some((id) => typeof id !== "string" || !/^\d+$/.test(id))) throw new Error("بيانات الكوليكشنز غير مكتملة.");
        if (page > 1 && (data.pages !== pages || data.total !== total)) throw new Error("اتغيرت الموديلات أثناء التحميل. حاول تاني.");
        pages = data.pages; total = data.total;
        data.productIds.forEach((id) => ids.add(id));
      }
      if (ids.size !== total) throw new Error("بيانات الكوليكشنز غير مكتملة.");
      collections[index] = {...item, productIds: ids};
    }
  }
  await Promise.all(Array.from({length: Math.min(3, selected.length)}, worker));
  const allowed = new Set(collections.flatMap((item) => [...item.productIds]));
  return {catalog: catalog.filter((product) => allowed.has(String(product.id))), collections};
}

function renderCatalogTools() {
  const categories = $("#catalog-categories");
  const grouped = new Map();
  const products = enabledCatalog();
  if (root.dataset.collectionMode === "shopify") {
    state.collections.forEach((item) => {
      const members = products.filter((product) => item.productIds.has(String(product.id)));
      grouped.set(item.id, {title: item.title, count: members.length, imageUrl: item.imageUrl || members.find((product) => product.imageUrl)?.imageUrl || ""});
    });
  } else products.forEach((product) => {
    const key = productCategory(product);
    const current = grouped.get(key) || { count: 0, imageUrl: "" };
    current.count += 1;
    if (!current.imageUrl && product.imageUrl) current.imageUrl = product.imageUrl;
    grouped.set(key, current);
  });
  categories.replaceChildren();
  if (state.catalogFilter !== "all" && !grouped.has(state.catalogFilter)) state.catalogFilter = "all";
  const groups = [["all", { count: products.length, imageUrl: products.find((product) => product.imageUrl)?.imageUrl || "" }], ...grouped.entries()];
  groups.forEach(([key, data]) => {
    const button = node("button", `category-tab collection-circle${state.catalogFilter === key ? " active" : ""}`);
    button.type = "button";
    button.setAttribute("aria-pressed", String(state.catalogFilter === key));
    const visual = node("span", "collection-circle-image");
    if (data.imageUrl) {
      const image = node("img"); image.src = data.imageUrl; image.alt = ""; image.loading = "lazy";
      image.addEventListener("error", () => image.remove()); visual.append(image);
    } else visual.append(node("span", "collection-circle-fallback", "W"));
    const caption = node("span", "collection-circle-caption");
    const count = node("span", "collection-circle-count", String(data.count));
    count.setAttribute("aria-label", `${data.count} موديل`);
    caption.append(node("span", "collection-circle-label", key === "all" ? "كل الموديلات" : (data.title || key)), count);
    button.append(visual, caption);
    button.addEventListener("click", () => { state.catalogFilter = key; state.detailProductId = null; state.guided.step = 1; state.guided.productId = null; renderCatalog(); });
    categories.append(button);
  });
  root.querySelectorAll(".view-button").forEach((button) => {
    const active = button.dataset.view === state.catalogMode;
    button.classList.toggle("active", active);
    button.setAttribute("aria-pressed", String(active));
    button.onclick = () => { state.catalogMode = button.dataset.view; state.detailProductId = null; renderCatalog(); };
  });
}

function renderCards(products, grid) {
  grid.className = "catalog-grid";
  products.forEach((product) => {
    const card = node("article", "product-card");
    const imageBox = node("button", "product-image product-detail-trigger");
    imageBox.type = "button"; imageBox.setAttribute("aria-label", `عرض تفاصيل ${product.name}`);
    imageBox.addEventListener("click", () => { state.detailProductId = product.id; renderCatalog(); });
    const fallback = node("div", "image-fallback", "W");
    imageBox.append(fallback);
    if (product.imageUrl) {
      const image = node("img"); image.alt = product.name; image.loading = "lazy"; image.src = product.imageUrl;
      image.addEventListener("load", () => { fallback.hidden = true; });
      image.addEventListener("error", () => image.remove());
      imageBox.append(image);
    }
    const body = node("div", "product-body");
    const title = node("button", "product-title-link", product.name); title.type = "button";
    title.addEventListener("click", () => { state.detailProductId = product.id; renderCatalog(); });
    const heading = node("h2", Array.from(product.name.trim()).length > 24 ? "product-name-long" : ""); heading.append(title);
    body.append(node("div", "product-code", product.modelCode), heading);
    body.append(node("div", "product-meta", `الثري: ${product.packSize} قطع`));
    body.append(renderGroupedSizePrices(product, "product-size-prices"));
    const badge = renderBadge(product); if (badge) body.append(badge);
    const discountLine = renderDiscountLine(product, true); if (discountLine) body.append(discountLine);
    const price = node("div", "price-row");
    price.append(node("strong", "", `الثري ${money(product.threePrice, product.currency)} · ${product.packSize} قطع`));
    body.append(price);
    const add = node("button", "button secondary wide", product.available ? "إضافة الثري للسلة" : "غير متاح للطلب");
    add.type = "button"; add.disabled = !product.available || state.isSubmitting;
    add.addEventListener("click", () => dispatch({ type: "ADD_THREE", productId: product.id }));
    body.append(add); card.append(imageBox, body); grid.append(card);
  });
}

function sizePrice(size, product) {
  return size.unitPrice ?? product.unitPriceFrom;
}

function standardSizeRank(name) {
  const normalized = String(name || "").trim().toUpperCase().replaceAll(" ", "");
  const fixed = new Map([["XS", 0], ["S", 1], ["M", 2], ["L", 3], ["XL", 4], ["XXL", 5], ["2XL", 5]]);
  if (fixed.has(normalized)) return fixed.get(normalized);
  const match = normalized.match(/^([3-9]|[1-9][0-9]+)XL$/);
  return match ? Number(match[1]) + 3 : null;
}

function groupedSizePrices(product) {
  const entries = product.sizes.map((size, index) => ({
    index, name: size.name, price: Number(sizePrice(size, product)), rank: standardSizeRank(size.name),
  }));
  const rankCounts = new Map();
  entries.forEach((entry) => { if (entry.rank !== null) rankCounts.set(entry.rank, (rankCounts.get(entry.rank) || 0) + 1); });
  const byPrice = new Map();
  entries.forEach((entry) => {
    if (!byPrice.has(entry.price)) byPrice.set(entry.price, []);
    byPrice.get(entry.price).push(entry);
  });
  return [...byPrice.entries()].map(([price, priceEntries]) => {
    const standard = priceEntries.filter((entry) => entry.rank !== null && rankCounts.get(entry.rank) === 1).sort((a, b) => a.rank - b.rank);
    const explicit = priceEntries.filter((entry) => entry.rank === null || rankCounts.get(entry.rank) !== 1).sort((a, b) => a.index - b.index);
    const runs = [];
    standard.forEach((entry) => {
      const previous = runs.at(-1);
      if (previous && entry.rank === previous.endRank + 1) {
        previous.endName = entry.name; previous.endRank = entry.rank;
      } else {
        runs.push({ startName: entry.name, endName: entry.name, endRank: entry.rank });
      }
    });
    const labels = runs.map((run) => run.startName === run.endName ? run.startName : `${run.startName}–${run.endName}`);
    explicit.forEach((entry) => labels.push(entry.name));
    return { label: labels.join("، "), price };
  });
}

function renderGroupedSizePrices(product, extraClass = "") {
  const legend = node("div", `grouped-size-prices${extraClass ? ` ${extraClass}` : ""}`);
  legend.setAttribute("aria-label", "أسعار المقاسات");
  groupedSizePrices(product).forEach((group) => {
    const detailClass = extraClass.split(" ").includes("detail-sizes") ? " detail-size-row" : "";
    const item = node("span", `grouped-size-price product-size-price${detailClass}`);
    const range = node("b", "size-range-label", group.label);
    range.dir = "ltr";
    item.append(range, node("span", "size-range-price", `: ${money(group.price, product.currency)}`));
    legend.append(item);
  });
  return legend;
}

function setTableQuantity(productId, variantId, value) {
  try {
    if (!state.session.allowSizeQuantities) return;
    if (!Number.isSafeInteger(value) || value < 0) throw new RangeError("Size quantity must be a nonnegative integer");
    if (value === 0 && state.cart.items[productId]?.mode !== "sizes") return;
    if (!state.cart.items[productId]) state.cart = cartReducer(state.cart, { type: "ADD_THREE", productId });
    if (state.cart.items[productId].mode !== "sizes") state.cart = cartReducer(state.cart, { type: "SET_MODE", productId, mode: "sizes" });
    state.cart = cartReducer(state.cart, { type: "SET_SIZE", productId, variantId, value });
    state.quote = null; state.pendingOrderKey = null; state.quoteRevision += 1; state.lastCartEditAt = Date.now();
    renderCatalog(); renderCart(); renderQuote(); scheduleQuote();
  } catch (error) { message(cartError(error), "error"); }
}

function renderQuickOrderTable(products, grid) {
  grid.className = "quick-order-table";
  const sizeNames = [...new Set(products.flatMap((product) => product.sizes.map((size) => size.name)))];
  const preferred = ["M", "L", "XL", "XXL", "3XL", "4XL", "5XL"];
  const sizes = [...preferred.filter((name) => sizeNames.includes(name)), ...sizeNames.filter((name) => !preferred.includes(name))];
  const table = node("div", "quick-table");
  table.style.setProperty("--quick-size-count", String(sizes.length));
  const head = node("div", "quick-row quick-head");
  const modelHead = node("div", "quick-model", "الموديل"); modelHead.colSpan = 1; head.append(modelHead);
  sizes.forEach((name) => { const cell = node("div", "quick-size-head"); cell.append(node("strong", "", name)); head.append(cell); });
  head.append(node("div", "quick-total-head", "الإجمالي")); table.append(head);
  products.forEach((product) => {
    const row = node("div", "quick-row");
    const model = node("div", "quick-model");
    const thumb = node("img", "quick-thumb"); thumb.alt = product.name; thumb.loading = "lazy"; thumb.src = product.imageUrl || "";
    thumb.addEventListener("error", () => thumb.remove());
    const imageLink = node("button", "quick-detail-trigger"); imageLink.type = "button"; imageLink.setAttribute("aria-label", `عرض تفاصيل ${product.name}`); imageLink.append(thumb);
    imageLink.addEventListener("click", () => { state.detailProductId = product.id; renderCatalog(); });
    const nameLink = node("button", "quick-name-link", product.name); nameLink.type = "button";
    nameLink.addEventListener("click", () => { state.detailProductId = product.id; renderCatalog(); });
    const info = node("div", "quick-model-info"); info.append(node("strong", "", product.modelCode), nameLink, node("small", "", `${productCategory(product)} · ${product.packSize} قطع`), node("small", "size-price-heading", "سعر القطعة"), renderGroupedSizePrices(product, "quick-size-prices"));
    model.append(imageLink, info); row.append(model);
    sizes.forEach((name) => {
      const size = product.sizes.find((candidate) => candidate.name === name);
      const cell = node("div", "quick-size-cell");
      if (!size) { cell.append(node("span", "quick-unavailable", "—")); row.append(cell); return; }
      const cartItem = state.cart.items[product.id];
      const input = quantityInput(cartItem?.mode === "sizes" ? (cartItem.quantities[size.id] || 0) : 0, (value) => setTableQuantity(product.id, size.id, value));
      input.className = "quick-quantity"; input.disabled = state.isSubmitting || !state.session.allowSizeQuantities || size.available === false; input.setAttribute("aria-label", `${product.name} مقاس ${name}`);
      cell.append(input); row.append(cell);
    });
    const total = node("div", "quick-total");
    const add = node("button", "button secondary quick-add", "إضافة الثري للسلة"); add.type = "button"; add.disabled = state.isSubmitting || !product.available; add.addEventListener("click", () => dispatch({ type: "ADD_THREE", productId: product.id }));
    total.append(add); row.append(total); table.append(row);
  });
  grid.append(table);
}

function renderProductDetail(product, grid) {
  grid.className = "product-detail-view";
  const back = node("button", "detail-back", "الرجوع للمنتجات"); back.type = "button";
  back.addEventListener("click", () => { state.detailProductId = null; renderCatalog(); });
  const layout = node("article", "product-detail-layout");
  const imageBox = node("div", "product-detail-image");
  if (product.imageUrl) {
    const image = node("img"); image.src = product.imageUrl; image.alt = product.name;
    image.addEventListener("error", () => image.remove()); imageBox.append(image);
  } else imageBox.append(node("div", "image-fallback", "W"));
  const info = node("div", "product-detail-info");
  info.append(node("div", "product-code", product.modelCode), node("h2", "", product.name),
    node("p", "product-meta", `الثري: ${product.packSize} قطع`));
  const sizes = renderGroupedSizePrices(product, "detail-sizes");
  const three = node("div", "detail-three-price");
  three.append(node("span", "", `سعر الثري · ${product.packSize} قطع`), node("strong", "", money(product.threePrice, product.currency)));
  const add = node("button", "button primary wide", "إضافة الثري للسلة"); add.type = "button";
  add.disabled = state.isSubmitting || !product.available;
  add.addEventListener("click", () => dispatch({ type: "ADD_THREE", productId: product.id }));
  const detailBadge = renderBadge(product), detailDiscount = renderDiscountLine(product, true);
  info.append(...[detailBadge, node("h3", "", "سعر القطعة"), sizes, detailDiscount, three, add].filter(Boolean));
  layout.append(imageBox, info); grid.append(back, layout);
}

function guidedDraft(product) {
  const key = String(product.id);
  if (!state.guided.drafts[key]) {
    const current = state.cart.items[product.id] || state.cart.items[key];
    state.guided.drafts[key] = current ? {
      mode: current.mode, threeCount: current.threeCount || 1, quantities: { ...(current.quantities || {}) },
    } : { mode: "three", threeCount: 1, quantities: {} };
  }
  return state.guided.drafts[key];
}

function guidedValid(draft) {
  if (draft.mode === "three") return Number.isSafeInteger(draft.threeCount) && draft.threeCount > 0;
  if (!state.session.allowSizeQuantities) return false;
  return Object.values(draft.quantities).some((quantity) => Number.isSafeInteger(quantity) && quantity > 0);
}

function guidedInput(value, minimum, onValid) {
  const input = quantityInput(value, (next) => {
    if (!Number.isSafeInteger(next) || next < minimum) {
      message(minimum ? "عدد الثري لازم يكون واحد أو أكتر." : "اكتب كمية صحيحة من غير كسور.", "error");
      input.value = String(value); return;
    }
    message(""); onValid(next); renderCatalog();
  }, minimum);
  return input;
}

function renderGuided(products, grid) {
  grid.className = "guided-order";
  const track = node("ol", "guided-track");
  ["اختيار الموديل", "تحديد الكمية", "مراجعة وإضافة"].forEach((label, index) => {
    const item = node("li", state.guided.step === index + 1 ? "active" : "", label);
    if (state.guided.step === index + 1) item.setAttribute("aria-current", "step");
    track.append(item);
  });
  grid.append(track);
  if (state.guided.step === 1 || !state.guided.productId) {
    const choices = node("div", "guided-products");
    products.forEach((product) => {
      const button = node("button", "guided-product"); button.type = "button";
      if (product.imageUrl) { const image = node("img"); image.src = product.imageUrl; image.alt = ""; image.loading = "lazy"; button.append(image); }
      button.append(node("strong", "", product.name), node("small", "", `${product.modelCode} · ${product.packSize} قطع`));
      button.addEventListener("click", () => { guidedDraft(product); state.guided.productId = product.id; state.guided.step = 2; renderCatalog(); });
      choices.append(button);
    });
    grid.append(node("h2", "guided-title", "اختار الموديل"), choices); return;
  }
  const product = productById(state.guided.productId);
  if (!product) { state.guided = { step: 1, productId: null, drafts: state.guided.drafts }; renderCatalog(); return; }
  const draft = guidedDraft(product);
  const panel = node("section", "guided-panel");
  panel.append(node("p", "eyebrow", `${product.modelCode} · ${product.name}`));
  if (state.guided.step === 2) {
    panel.append(node("h2", "guided-title", "حدد طريقة وكمية الطلب"));
    const modes = node("div", "mode-switch guided-mode");
    [["three", "بالثري"], ["sizes", "بالمقاس"]].forEach(([mode, label]) => {
      const button = node("button", draft.mode === mode ? "active" : "", label); button.type = "button";
      button.setAttribute("aria-pressed", String(draft.mode === mode));
      button.disabled = mode === "sizes" && !state.session.allowSizeQuantities;
      button.addEventListener("click", () => { draft.mode = mode; renderCatalog(); }); modes.append(button);
    });
    panel.append(modes);
    if (draft.mode === "three") {
      const line = node("label", "guided-quantity"); line.append(node("span", "", `عدد الثري · ${product.packSize} قطع لكل ثري`),
        guidedInput(draft.threeCount, 1, (value) => { draft.threeCount = value; })); panel.append(line);
    } else {
      panel.append(node("div", "size-price-heading", "سعر القطعة"), renderGroupedSizePrices(product, "guided-size-prices"));
      const sizeGrid = node("div", "guided-size-grid");
      product.sizes.forEach((size) => {
        const line = node("label", "guided-size");
        line.append(node("strong", "", size.name), guidedInput(draft.quantities[size.id] || 0, 0, (value) => {
            if (value > 0) draft.quantities[size.id] = value; else delete draft.quantities[size.id];
          })); sizeGrid.append(line);
      }); panel.append(sizeGrid);
    }
    const navigation = node("div", "guided-navigation");
    const previous = node("button", "button secondary", "السابق"); previous.type = "button";
    previous.addEventListener("click", () => { state.guided.step = 1; renderCatalog(); });
    const next = node("button", "button primary", "مراجعة الاختيار"); next.type = "button"; next.disabled = !guidedValid(draft);
    next.addEventListener("click", () => { if (guidedValid(draft)) { state.guided.step = 3; renderCatalog(); } });
    navigation.append(previous, next); panel.append(navigation);
  } else {
    panel.append(node("h2", "guided-title", "راجع الاختيار قبل الإضافة"));
    const review = node("div", "guided-review");
    review.append(node("strong", "", draft.mode === "three" ? `${draft.threeCount} ثري · ${draft.threeCount * product.packSize} قطع` : "كميات بالمقاس"));
    if (draft.mode === "sizes") {
      review.append(node("div", "size-price-heading", "سعر القطعة"), renderGroupedSizePrices(product, "guided-size-prices"));
      product.sizes.forEach((size) => {
        const quantity = draft.quantities[size.id] || 0;
        if (quantity > 0) review.append(node("span", "", `${size.name} × ${quantity}`));
      });
    }
    const navigation = node("div", "guided-navigation");
    const previous = node("button", "button secondary", "تعديل الكميات"); previous.type = "button";
    previous.addEventListener("click", () => { state.guided.step = 2; renderCatalog(); });
    const commit = node("button", "button primary", "إضافة الاختيار للسلة"); commit.type = "button"; commit.disabled = !guidedValid(draft);
    commit.addEventListener("click", () => {
      if (!guidedValid(draft)) return;
      dispatch({ type: "UPSERT_ITEM", productId: product.id, item: draft });
      message("اتحفظ اختيار الموديل في السلة من غير تكرار.", "success");
    });
    navigation.append(previous, commit); panel.append(review, navigation);
  }
  grid.append(panel);
}

function renderCatalog() {
  renderCatalogTools();
  const grid = $("#catalog-grid"); grid.replaceChildren();
  if (state.detailProductId !== null) {
    const product = productById(state.detailProductId);
    if (product) { renderProductDetail(product, grid); return; }
    state.detailProductId = null;
  }
  const products = visibleCatalog();
  if (!products.length) { grid.className = "catalog-grid"; grid.append(node("div", "empty-card", "لا توجد منتجات متاحة في التصنيف ده حاليًا.")); return; }
  if (state.catalogMode === "table") renderQuickOrderTable(products, grid);
  else if (state.catalogMode === "guided") renderGuided(products, grid);
  else renderCards(products, grid);
}

function quantityInput(value, onChange, min = 0) {
  const input = node("input"); input.type = "number"; input.min = String(min); input.step = "1"; input.inputMode = "numeric"; input.value = String(value);
  input.addEventListener("change", () => onChange(Number(input.value)));
  return input;
}

function renderCart() {
  const container = $("#cart-items"); container.replaceChildren();
  const entries = Object.entries(state.cart.items);
  $("#cart-count").textContent = String(entries.length);
  $("#mobile-cart-jump").hidden = !entries.length;
  $("#mobile-cart-jump").textContent = `عرض السلة · ${entries.length} موديل`;
  if (!entries.length) {
    container.append(node("div", "empty-card", "السلة فاضية. أضف موديل بالثري للبدء."));
    $("#quote-summary").hidden = true; $("#quote-state").textContent = ""; $("#submit-order").disabled = true;
    return;
  }
  entries.forEach(([productId, item]) => {
    const product = productById(productId); if (!product) return;
    const block = node("div", "cart-item"); const head = node("div", "cart-item-head");
    head.append(node("strong", "", `${product.modelCode} · ${product.name}`));
    const remove = node("button", "", "حذف"); remove.type = "button"; remove.addEventListener("click", () => dispatch({ type: "REMOVE", productId })); head.append(remove); block.append(head);
    if (state.session.allowSizeQuantities) {
      const modes = node("div", "mode-switch");
      [["three", "بالثري"], ["sizes", "بالمقاس"]].forEach(([mode, label]) => { const button = node("button", item.mode === mode ? "active" : "", label); button.type = "button"; button.addEventListener("click", () => dispatch({ type: "SET_MODE", productId, mode })); modes.append(button); });
      block.append(modes);
      block.append(node("p", "size-mode-note", item.mode === "sizes"
        ? "اكتب الكمية لكل مقاس. التبديل للثري يحتفظ باختياراتك هنا، والطلب يستخدم الوضع الظاهر وقت الإرسال."
        : "تقدر تبدّل للمقاسات، وهنحتفظ مؤقتًا بعدد الثري. الطلب يستخدم الوضع الظاهر وقت الإرسال."));
    }
    if (item.mode === "three") {
      const line = node("label", "quantity-line"); line.append(node("span", "", `عدد الثري · ${product.packSize} قطع لكل ثري`), quantityInput(item.threeCount, (value) => safeDispatch({ type: "SET_THREE", productId, value }), 1)); block.append(line);
    } else {
      const sizeGrid = node("div", "size-grid");
      product.sizes.forEach((size) => { const line = node("label", "quantity-line"); line.append(node("span", "", size.name), quantityInput(item.quantities[size.id] || 0, (value) => safeDispatch({ type: "SET_SIZE", productId, variantId: size.id, value }))); sizeGrid.append(line); }); block.append(sizeGrid);
    }
    container.append(block);
  });
  container.querySelectorAll("button, input").forEach((control) => { control.disabled = state.isSubmitting; });
}

function renderQuote() {
  const summary = $("#quote-summary"); summary.replaceChildren();
  if (!state.quote) { summary.hidden = true; $("#submit-order").disabled = true; return; }
  const rows = [["إجمالي القطع", state.quote.totalQuantity], ["قبل الخصم", money(state.quote.subtotal, state.quote.currency)], [Number(state.quote.discountPercent) > 0 ? `خصمك ${state.quote.discountPercent}%` : "الخصم", money(state.quote.discount, state.quote.currency)]];
  if (Number(state.quote.tax) > 0) rows.push(["الضريبة", money(state.quote.tax, state.quote.currency)]);
  rows.push(["الإجمالي", money(state.quote.total, state.quote.currency)]);
  rows.forEach(([label, value], index) => {
    const row = node("div", `summary-row${index === rows.length - 1 ? " total" : ""}`); row.append(node("span", "", label), node("strong", "", String(value))); summary.append(row);
  });
  summary.hidden = false; $("#submit-order").disabled = state.isSubmitting;
}

function cartError(error) {
  if (error.message.includes("at least one size")) return "اختار كمية لمقاس واحد على الأقل.";
  return "اكتب كمية صحيحة من غير كسور. عدد الثري لازم يكون واحد أو أكتر.";
}
function safeDispatch(action) { try { dispatch(action); } catch (error) { message(cartError(error), "error"); renderCart(); } }
function dispatch(action) {
  if (state.isSubmitting) return;
  state.cart = cartReducer(state.cart, action); state.quote = null; state.pendingOrderKey = null;
  state.quoteRevision += 1; state.lastCartEditAt = Date.now();
  renderCart(); renderQuote(); scheduleQuote();
}

let quoteTimer;
function scheduleQuote() {
  clearTimeout(quoteTimer);
  if (!Object.keys(state.cart.items).length) { state.quoteWanted = false; return; }
  const delay = Math.max(0, 400 - (Date.now() - state.lastCartEditAt));
  quoteTimer = setTimeout(refreshQuote, delay);
}
async function refreshQuote() {
  if (!Object.keys(state.cart.items).length) return;
  if (state.quoteInFlight) { state.quoteWanted = true; return; }
  let items;
  try { items = quoteItems(state.cart); } catch (error) { $("#quote-state").textContent = cartError(error); $("#quote-state").className = "quote-state error"; return; }
  const revision = state.quoteRevision;
  const epoch = sessionEpoch;
  state.quoteInFlight = true; state.quoteWanted = false;
  $("#quote-state").textContent = "جاري تحديث الأسعار الخاصة بحسابك…"; $("#quote-state").className = "quote-state";
  try {
    const quote = await api("quote", { method: "POST", body: JSON.stringify({ items }) });
    if (epoch !== sessionEpoch || revision !== state.quoteRevision || !Object.keys(state.cart.items).length) return;
    state.quote = quote; state.quoteAt = Date.now(); $("#quote-state").textContent = quote.pricedAt ? `آخر تحديث للأسعار: ${readableDate(quote.pricedAt)}` : "تم تحديث الأسعار الخاصة بحسابك"; renderQuote();
  } catch (error) {
    if (epoch !== sessionEpoch || revision !== state.quoteRevision) return;
    state.quote = null; $("#quote-state").textContent = error.message; $("#quote-state").className = "quote-state error"; renderQuote();
  } finally {
    if (epoch !== sessionEpoch) return;
    state.quoteInFlight = false;
    if ((state.quoteWanted || revision !== state.quoteRevision) && Object.keys(state.cart.items).length) scheduleQuote();
  }
}

function renderOrders() {
  const list = $("#orders-list"); list.replaceChildren();
  if (!state.orders.length) { list.append(node("div", "empty-card", "لسه مفيش طلبات مرسلة للمراجعة.")); return; }
  state.orders.forEach((order) => {
    const card = node("article", "order-card"); const name = node("div");
    name.append(node("strong", "", order.orderName), node("small", "", ` · ${readableDate(order.createdAt)}`));
    if (order.source) name.append(node("small", "", order.source === "whatsapp" ? "من واتساب" : "من الموقع"));
    if (order.updatedAt) name.append(node("small", "", `آخر تحديث: ${readableDate(order.updatedAt)}`));
    card.append(name, node("span", "status-chip", order.stateLabel), node("strong", "", money(order.total, order.currency))); list.append(card);
  });
}

async function loadOrders(epoch = sessionEpoch) {
  try {
    const result = await api("orders");
    if (epoch !== sessionEpoch) return;
    state.orders = result.orders || []; renderOrders();
  } catch (error) {
    if (epoch !== sessionEpoch) return;
    $("#orders-list").replaceChildren(node("div", "empty-card", error.message));
  }
}

const QUOTE_REFRESH_AFTER_MS = 55 * 60 * 1000;   // server quotes are valid for 60 min; re-price before sending if ours is older than 55
const PRICE_CHANGED_TEXT = "الأسعار أو المقاسات المتاحة اتغيرت — راجع السلة ودوس إرسال تاني";
const GENERIC_SUBMIT_ERROR = "تعذر إرسال الطلب. حاول تاني بعد شوية.";

// Submit problems must ALWAYS be visible: Arabic text in the message bar (scrolled into view) and next to the send button.
function showSubmitError(text) {
  const shown = /[\u0600-\u06FF]/.test(String(text || "")) ? String(text) : GENERIC_SUBMIT_ERROR;
  message(shown, "error");
  const quoteState = $("#quote-state"); quoteState.textContent = shown; quoteState.className = "quote-state error";
  try { $("#global-message").scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* old browsers */ }
  return shown;
}

async function fetchFreshQuote() {
  return api("quote", { method: "POST", body: JSON.stringify({ items: quoteItems(state.cart) }) });
}

// The server said prices/sizes changed: take its fresh quote (or ask for one) so the summary shows the new totals without a reload.
async function adoptFreshQuote(error, epoch) {
  state.pendingOrderKey = null;                      // nothing was created; the next send is a new attempt
  let fresh = error.quote && error.quote.quoteToken ? error.quote : null;
  let problem = "";
  if (!fresh) { try { fresh = await fetchFreshQuote(); } catch (inner) { problem = inner.message; } }
  if (epoch !== sessionEpoch) return "";
  state.quote = fresh; state.quoteAt = fresh ? Date.now() : 0;
  renderQuote();
  return problem;
}

async function submitOrder() {
  if (!state.quote || state.isSubmitting) return;
  const epoch = sessionEpoch;
  const button = $("#submit-order"); button.disabled = true; button.textContent = "جاري الإرسال…";
  state.isSubmitting = true; renderCatalog(); renderCart(); renderQuote(); button.textContent = "جاري الإرسال…";
  state.pendingOrderKey ||= crypto.randomUUID();
  try {
    if (Date.now() - (state.quoteAt || 0) > QUOTE_REFRESH_AFTER_MS) {
      // old quote: re-price first; if the total moved the customer must see it before anything is sent
      const previousTotal = state.quote.total;
      const fresh = await fetchFreshQuote();
      if (epoch !== sessionEpoch) return;
      state.quote = fresh; state.quoteAt = Date.now(); renderQuote();
      if (fresh.total !== previousTotal) { state.pendingOrderKey = null; showSubmitError(PRICE_CHANGED_TEXT); return; }
    }
    const result = await api("orders", { method: "POST", body: JSON.stringify({ quoteToken: state.quote.quoteToken, idempotencyKey: state.pendingOrderKey }) });
    if (epoch !== sessionEpoch) return;
    // merged = the order was added to the customer's still-open quote (the server's notice already names the order and says so)
    message(result.merged && result.notice
      ? `${result.notice}.${result.replayed ? " (تم تأكيد نفس المحاولة السابقة)" : ""}`
      : `تم إرسال الطلب ${result.orderName} للمراجعة${result.replayed ? " — تم تأكيد نفس المحاولة السابقة" : ""}.${result.notice ? ` تنبيه: ${result.notice}.` : ""}`, "success");
    try { $("#global-message").scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* ignore */ }
    state.cart = emptyCart(); state.quote = null; state.quoteAt = 0; state.pendingOrderKey = null;
    state.guided = { step: 1, productId: null, drafts: {} }; state.detailProductId = null;
    renderCart(); renderQuote(); await loadOrders(epoch);
  } catch (error) {
    if (epoch !== sessionEpoch) return;
    let text = error instanceof TypeError || error instanceof RangeError ? cartError(error) : error.message;
    if (error.code === "PRICE_CHANGED") { const problem = await adoptFreshQuote(error, epoch); if (epoch !== sessionEpoch) return; if (problem) text = problem; }
    showSubmitError(text);
  } finally {
    if (epoch !== sessionEpoch) return;
    state.isSubmitting = false; renderCatalog(); renderCart(); renderQuote(); button.textContent = "إرسال للمراجعة";
  }
}

async function loadApproved(epoch = sessionEpoch) {
  if (epoch !== sessionEpoch) return;
  $("#customer-name").textContent = state.session.customer?.name || "";
  const [catalog] = await Promise.all([api("catalog"), loadOrders(epoch)]);
  if (epoch !== sessionEpoch) return;
  const controller = new AbortController(); collectionLoadController = controller;
  const timeout = setTimeout(() => controller.abort(), 30000);
  let selection;
  try { selection = await loadCollectionSelection(validateCatalog(catalog.products), controller.signal); }
  finally { clearTimeout(timeout); if (collectionLoadController === controller) collectionLoadController = null; }
  if (epoch !== sessionEpoch) return;
  state.catalog = selection.catalog; state.collections = selection.collections;
  showView("#approved-view"); renderCatalog(); renderCart();
}

function clearPrivatePortalState() {
  sessionEpoch += 1;
  collectionLoadController?.abort(); collectionLoadController = null;
  clearTimeout(quoteTimer); quoteTimer = undefined;
  state.catalog = []; state.collections = []; state.orders = []; state.cart = emptyCart(); state.quote = null;
  state.quoteRevision += 1; state.quoteInFlight = false; state.quoteWanted = false; state.isSubmitting = false;
  state.pendingOrderKey = null; state.catalogMode = "cards"; state.catalogFilter = "all";
  state.detailProductId = null; state.guided = { step: 1, productId: null, drafts: {} };
  $("#customer-name").textContent = ""; $("#catalog-grid").replaceChildren(); $("#orders-list").replaceChildren();
  $("#cart-items").replaceChildren(); $("#quote-summary").replaceChildren(); $("#quote-summary").hidden = true;
  $("#quote-state").textContent = ""; $("#quote-state").className = "quote-state";
  $("#submit-order").disabled = true; $("#submit-order").textContent = "إرسال للمراجعة";
}

function showAccess() {
  clearPrivatePortalState();
  showView("#access-view");
  $("[data-wella-phone-login]").hidden = false;
  $("#session-chip").textContent = "دخول برقم واتساب";
}

async function start() {
  clearPrivatePortalState();
  const epoch = ++sessionEpoch;
  showView("#loading-view"); message(""); $("#catalog-retry").hidden = true; $("#loading-view .spinner").hidden = false;
  if (root.dataset.connectionReady === "false") {
    $("#loading-view .spinner").hidden = true;
    $("#loading-view h1").textContent = "بوابة عملاء الجملة";
    $("#loading-view p").textContent = "البوابة قيد التجهيز. الدخول هيتاح للعملاء المعتمدين بعد التفعيل.";
    return;
  }
  try {
    const session = await api("session");
    if (epoch !== sessionEpoch) return;
    state.session = session;
    if (!["test", "live"].includes(state.session.environment)) throw new Error("البوابة غير متاحة حاليًا.");
    if (state.session.state === "approved") { $("#session-chip").textContent = "عميل جملة معتمد"; await loadApproved(epoch); }
    else if (state.session.state === "anonymous" || state.session.state === "pending") showAccess();
    else if (state.session.state === "suspended") { clearPrivatePortalState(); showView("#suspended-view"); $("#session-chip").textContent = "متوقف"; }
    else throw new Error("حالة الحساب غير معروفة.");
  } catch (error) {
    if (epoch !== sessionEpoch) return;
    clearPrivatePortalState(); showView("#loading-view"); $("#loading-view .spinner").hidden = true; $("#loading-view h1").textContent = "تعذر فتح بوابة الجملة"; $("#loading-view p").textContent = "تعذر تحميل الكتالوج. حاول تاني."; $("#catalog-retry").hidden = false; message("راجع الاتصال وحاول تاني.", "error");
  }
}

$("#catalog-retry").addEventListener("click", start);
$("#wholesale-logout").addEventListener("click", () => window.wellaWholesalePhoneAuth.logout());
$("#submit-order").addEventListener("click", submitOrder);
$("#mobile-cart-jump").addEventListener("click", () => {
  root.querySelector('.nav-tab[data-target="catalog-panel"]').click();
  $("#cart-panel").scrollIntoView({behavior: "auto", block: "start"});
  $("#cart-panel").focus({preventScroll: true});
});
root.querySelectorAll(".nav-tab").forEach((tab) => tab.addEventListener("click", () => {
  root.querySelectorAll(".nav-tab").forEach((item) => item.classList.toggle("active", item === tab));
  root.querySelectorAll(".portal-panel").forEach((panel) => { panel.hidden = panel.id !== tab.dataset.target; });
  if (tab.dataset.target === "orders-panel") loadOrders();
}));

if (phoneMode) {
  state.session = {state: "anonymous"};
  root.addEventListener("wella:phone-login", () => start());
  root.addEventListener("wella:phone-logout", () => {
    state.session = {state: "anonymous"}; clearPrivatePortalState(); showAccess();
  });
  // Persistent session: a page refresh re-adopts the saved token and validates it through the normal session/catalog calls.
  // LOGIN_REQUIRED (expired / revoked / no longer approved) makes wella-phone-login.js clear the token and fire wella:phone-logout -> OTP form.
  let restored = false;
  try { restored = Boolean(window.wellaWholesalePhoneAuth?.restore?.()); } catch { restored = false; }
  if (restored) start(); else showAccess();
} else start();

})();
